import React from 'react';
import { Alert, AlertTitle } from '@mui/material';
import { summarizeDeckGeneration } from './documentMeta';

/** Visible marker the worker puts in a slot it could not fill. */
const NEEDS_INPUT_RE = /\[Needs input:/g;

/**
 * Number of "[Needs input: ...]" markers in HTML.
 * @param {string} html - HTML.
 * @returns {number}
 */
export function countNeedsInputMarkers(html) {
  if (typeof html !== 'string' || !html) return 0;
  return (html.match(NEEDS_INPUT_RE) || []).length;
}

/**
 * Warning shown where a generated deck is opened when some slide slots could not be filled. Those
 * slots carry a visible "[Needs input: ...]" marker on the slide (the worker never fills them with
 * placeholder names), so the user knows to replace them before sharing or exporting.
 *
 * With `html` (the deck as it is now, in the editor) the count follows the markers that are still
 * there, and the notice goes away once the user has replaced all of them.
 * @param {object} props - Props.
 * @param {{trustMetadata?: object|null, trust_metadata?: object|null}|null} props.doc - Generated document.
 * @param {string} [props.html] - Current deck HTML.
 * @param {object} [props.sx] - Alert styles.
 * @returns {JSX.Element|null} Notice, or null when the deck has nothing to warn about.
 */
export default function DeckGenerationNotice({ doc, html, sx }) {
  const summary = summarizeDeckGeneration(doc);
  if (!summary) return null;
  const { total, aiFailed } = summary;
  let { fallbackSlots } = summary;
  if (typeof html === 'string') {
    const markers = countNeedsInputMarkers(html);
    if (markers === 0) return null;
    fallbackSlots = Math.max(1, Math.min(markers, fallbackSlots || markers));
  }
  const title = `${fallbackSlots} of ${Math.max(total, fallbackSlots)} slide slot${total === 1 ? '' : 's'} need${fallbackSlots === 1 ? 's' : ''} input`;
  return (
    <Alert severity="warning" role="status" sx={sx} data-testid="deck-generation-notice">
      <AlertTitle>{title}</AlertTitle>
      {aiFailed ? 'The AI request failed for part of this deck. ' : ''}
      {'Slots the AI could not fill show "[Needs input: ...]" on the slide. Replace them before you share or export the deck.'}
    </Alert>
  );
}
